import { Select, SelectItem } from "@nextui-org/react";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import comentIcon from "../../assets/comentIcon.svg";
import logoIvi from "../../assets/logo.svg";
import question from "../../assets/question.svg";
import ButtonComponent from "../../components/ButtonComponent";
import LeftHomeComponent from "../../components/leftHomeComponent/LeftHomeComponent";
import MainHomeComponent from "../../components/MainHomeComponent/MainHomeComponent";
import RightHomeComponent from "../../components/RightHomeComponent/RightHomeComponent";
import "./home.css"; // Certifique-se de importar o arquivo CSS
import { useIsMutating, useMutation, useQuery } from "@tanstack/react-query";
import { changeStatusLead, getDistribuidoras, updateDistribuidora } from "../../services/onboarding";
import {
  GET_DISTRIBUIDORAS,
  POST_GENERATE_TOKEN_FROM_SAFELINK,
  POST_PREPARE_TERM,
  POST_UPDATE_DISTRIBUIDORA,
} from "../../query/constants";
import { SessionContext } from "../../context/session";
import { getLocalStorage } from "../../utils/localStorage";
import LoadingFullPage from "../../components/Loading/LoadingFullPage/LoadingFullPage";
import { ScreenError } from "../../components/screenError";
import PdfAlert from "../../assets/installation/pdf-alert.png";
import { JaInicieiCadastroModal, JaInicieiMeuCadastroModalRef } from "./JaInicieiCadastroModal";
import { generateSignedTokenFromSafelink } from "../../services/authentication";
import { useGetProtectedLead } from "../../hooks/useGetProtectedLead";

import { useTenantStore } from "../../stores/tenant/use-tenant-store";
import { RedirectContext } from "../../context/redirect";
import { useInitAuth } from "../../hooks/useInitAuth";
import { FluxoType } from "../../stores/tenant/tenant.types";

enum Action {
  CONTINUE_REGISTRATION = "1",
  INVALIDATE_REGISTRATION = "2",
}

enum Source {
  WHATSAPP = "1",
  INSTAGRAM = "2",
  FACEBOOK = "3",
  EMAIL = "4",
  TELEGRAM = "5",
  LP = "6",
  LINKEDIN = "7",
}

const sourceMap: Record<string, string> = {
  [Source.WHATSAPP]: "WhatsApp",
  [Source.INSTAGRAM]: "Instagram",
  [Source.FACEBOOK]: "Facebook",
  [Source.EMAIL]: "Email",
  [Source.TELEGRAM]: "Telegram",
  [Source.LP]: "Landing Page",
  [Source.LINKEDIN]: "LinkedIn",
};

export const Home = () => {
  const { assets, type, texts } = useTenantStore();
  const { features: { isChoosePlanAllowed } } = useTenantStore();

  const navigate = useNavigate();
  const { setItemSession, getItemSession } = useContext(SessionContext);

  useEffect(() => {
    const parceiroData = getItemSession("DadosParceiro");
    if (type === FluxoType.CONSULTOR && !parceiroData) {
      navigate("/");
    }
  }, [getItemSession, type, navigate]);

  const [redirectEdit, setRedirectEdit] = useState<string>("");
  const [isLeadInValidacaoFatura, setIsLeadInValidacaoFatura] = useState<boolean>(false);
  const [isLeadReprovada, setIsLeadReprovada] = useState<boolean>(false);
  const [selectedDistributor, setSelectedDistributor] = useState("");
  const [searchParams] = useSearchParams();
  const [abriuComLinkDeAcesso] = useState(() => searchParams.has("token") || searchParams.has("signer-token"));
  const preparandoTermo = useIsMutating({ mutationKey: [POST_PREPARE_TERM] }) > 0;
  const modalRef = useRef<JaInicieiMeuCadastroModalRef | null>(null);
  const getProtectedLead = useGetProtectedLead({ retomarNoTermo: true });
  const { setContinueResgistration } = useContext(RedirectContext);
  const initAuth = useInitAuth();

  useEffect(() => {
    const localStoragePath = getLocalStorage("path");
    if (localStoragePath) {
      setRedirectEdit(localStoragePath);
    }

    const unsubscribeEmail = searchParams.get("unsubscribe-email");
    if (unsubscribeEmail != null) {
      putReproveLeadMutation.mutate(unsubscribeEmail);
      return;
    }

    const origem = searchParams.get("source");
    if (origem && sourceMap[origem]) {
      setItemSession("origem", sourceMap[origem]);
    }

    const token = searchParams.get("token");
    if (token !== null) {
      authenticateSafelinkTokenMutation.mutate(token);
    }

    const signerToken = searchParams.get("signer-token");
    if (signerToken !== null) {
      authenticateSafelinkTokenMutation.mutate(signerToken);
      setItemSession("isSigner", "true");
    }

    const code = searchParams.get("code");
    if (code !== null) {
      setItemSession("code", code);
    }

    const email = searchParams.get("email");
    const action = searchParams.get("action");

    if (email && action === Action.CONTINUE_REGISTRATION) {
      setContinueResgistration(true);
      setItemSession("emailContinue", email);
      initAuth.mutate({ email });
    }

    if (email && action === Action.INVALIDATE_REGISTRATION) {
      setItemSession("emailContinue", email);
      setItemSession("reproveLead", "true");
      initAuth.mutate({ email });
    }
  }, []);

  const authenticateSafelinkTokenMutation = useMutation({
    mutationFn: generateSignedTokenFromSafelink,
    mutationKey: [POST_GENERATE_TOKEN_FROM_SAFELINK],
    onSuccess: (data) => {
      const { token, email } = data;
      const isSigner = getItemSession("isSigner");
      if (token) {
        sessionStorage.setItem("token", data.token);
        getProtectedLead.mutate();
        return;
      }
      if (email && !isSigner) {
        modalRef.current?.open(email);
      }

      if (email && isSigner) {
        initAuth.mutate({ email });
        setItemSession("emailContinue", email);
        setContinueResgistration(true);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const putReproveLeadMutation = useMutation({
    mutationFn: changeStatusLead,
    mutationKey: ["put-reprove-lead"],
    onSuccess: (data) => {
      setIsLeadReprovada(true);
    },
    onError(erro) {
      console.log(erro);
    },
  });

  const updateDistribuidoraMutation = useMutation({
    mutationFn: updateDistribuidora,
    mutationKey: [POST_UPDATE_DISTRIBUIDORA],
    onSuccess: (data) => {
      if (data.status !== "SUCESSO") {
        throw new Error(data.status);
      }
      navigate("/installation");
    },
    onError: (error) => {
      const errorMessage = error.message;
      if (errorMessage === "INPUT_INVALIDO") {
        console.log(errorMessage);
      }
    },
  });

  const { data, isLoading, isError } = useQuery({
    queryFn: () => getDistribuidoras(),
    queryKey: [GET_DISTRIBUIDORAS],
    staleTime: 1000 * 60 * 60 * 60,
  });

  type DistribuidoraOption = {
    value: string;
    label: string;
  };

  let optionsGetApi: DistribuidoraOption[] = [];

  if (!isLoading && !isError && data) {
    optionsGetApi = data.map(({ id, nome }) => ({
      value: id,
      label: nome,
    }));
  }

  const options =
    type === FluxoType.CONSULTOR
      ? optionsGetApi
      : [
          ...optionsGetApi,
          {
            value: "select-your-distributor",
            label: "Não encontrei minha distribuidora",
          },
        ];

  const handleSelectChange = (selectedOption: any) => {
    const distribuidora = selectedOption.target.value;
    const filteredDistribuidora = data?.find((item) => item.id === distribuidora);
    if (filteredDistribuidora) {
      setItemSession("dados-distribuidora", JSON.stringify(filteredDistribuidora));
    }

    setItemSession("distribuidora", distribuidora);
    setSelectedDistributor(selectedOption.target.value); // Atualiza o estado com a opção selecionada
  };

  const handleButtonClick = () => {
    const distributorOptionSelected = selectedDistributor;
    const isDistrubutorOptionSelected = distributorOptionSelected !== "";
    const isDistributorNotAvailable = distributorOptionSelected === "select-your-distributor";
    const isFromEdit = redirectEdit !== "";

    if (isDistributorNotAvailable) {
      navigate("/select-your-distributor");
      return;
    }

    if (isDistrubutorOptionSelected && !isFromEdit && isChoosePlanAllowed) {
      navigate("/choose-plan");
      return;
    }

    if (isDistrubutorOptionSelected && !isFromEdit) {
      navigate("/contact");
      return;
    }

    if (isFromEdit) {
      const newDistribuidora = distributorOptionSelected;
      updateDistribuidoraMutation.mutate(newDistribuidora);
      return;
    }
  };

  const loading =
    (abriuComLinkDeAcesso && authenticateSafelinkTokenMutation.isIdle) ||
    authenticateSafelinkTokenMutation.isPending ||
    getProtectedLead.isPending ||
    preparandoTermo ||
    putReproveLeadMutation.isPending;

  return isLeadInValidacaoFatura ? (
    <ScreenError
      icon={PdfAlert}
      alt="Pdf com uma exclamação"
      texto="Você possui uma fatura em análise, em breve daremos um retorno."
      title="Validação de Fatura"
    />
  ) : isLeadReprovada ? (
    <ScreenError
      icon={PdfAlert}
      alt="Pdf com uma exclamação"
      texto="Seu cadastro na IVI foi cancelado. Caso mude de ideia, você pode contar com o nosso time de especialistas, que está sempre pronto para te auxiliar em todo o processo e construir uma relação de confiança e parceria!"
      title="Cadastro cancelado"
    />
  ) : (
    <MainHomeComponent>
      <LeftHomeComponent logoSrc={logoIvi} title={texts.home.title} description={texts.home.description} imgSrc={assets.home.image} />
      <RightHomeComponent
        cardHomeClass="container-home"
        questionSrc={question}
        commentIconSrc={comentIcon}
        title={texts.home.title}
        description={texts.home.description}
      >
        <div className="container-select-home">
          <Select
            onChange={handleSelectChange}
            label="Selecione sua distribuidora"
            className="max-w-xs select-inputs overflow-y-auto"
            style={{ width: "280px" }}
            classNames={{
              label: ["text-base text-black "],
            }}
          >
            {options.map((dest) => (
              <SelectItem key={dest.value} value={dest.value}>
                {dest.label}
              </SelectItem>
            ))}
          </Select>

          {type === FluxoType.AUTOSSERVICO && (
            <p className="p-cadastro-continue" onClick={() => modalRef.current?.open()}>
              {" "}
              Já iniciei meu cadastro
            </p>
          )}
          {selectedDistributor && (
            <ButtonComponent
              className="continue-button"
              // className="btn-homee"
              style={{
                marginTop: "16px",
              }}
              onClick={handleButtonClick}
              isDisabled={selectedDistributor === null}
            >
              Continuar
            </ButtonComponent>
          )}
        </div>

        {updateDistribuidoraMutation.isPending && <LoadingFullPage />}
        {initAuth.isPending && <LoadingFullPage />}

        <JaInicieiCadastroModal ref={modalRef} setIsLeadInValidacaoFatura={setIsLeadInValidacaoFatura} />
      </RightHomeComponent>
      {loading && <LoadingFullPage />}
    </MainHomeComponent>
  );
};
